import React from 'react';
import styled from 'styled-components';
import {motion} from 'framer-motion';
import {Link, useNavigate} from 'react-router-dom';
import Seo from '../../../shared/seo/Seo';

// Import components from their new locations in the feature-first architecture
import Hero from '../components/hero/Hero';
import ServicesCarousel from '../components/services_carousel/ServicesCarousel';
import AboutContent from '../components/about/AboutContent';
import Partners from '../components/partners/Partners';
import Clients from "../components/clients/Clients";
import Stats from '../components/stats/Stats';
import ShopProducts from '../components/shop_products/ShopProducts';
import {useTranslation} from "react-i18next";
import { getCanonicalUrl, pageMeta } from 'shared/seo/seoMeta';

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();


    const handleLinkClick = (path: string) => {
        window.scrollTo(0, 0);
        navigate(path);
    };
    return (
        <>
            <Seo
              title={pageMeta.home.title}
              description={pageMeta.home.description}
              canonical={getCanonicalUrl(pageMeta.home.canonicalPath)}
            />
            <Hero/>
            <ServicesCarousel/>

            <AboutSection className="section">
                <div className="container">
                    <AboutGrid>
                        <AboutImage
                            initial={{opacity: 0, x: -50}}
                            whileInView={{opacity: 1, x: 0}}
                            transition={{duration: 0.8}}
                            viewport={{once: true}}
                        >
                            <img src='/images/background/about-bg.webp' alt="About Africa Supply Service Sarl"/>

                        </AboutImage>

                        <AboutContent/>
                    </AboutGrid>
                </div>
            </AboutSection>

            <ShopProducts/>

            <Partners/>
            <Clients/>

            <Stats className="section"/>

            <CtaSection>
                <div className="container">
                    <CtaContent>
                        <motion.div
                            initial={{opacity: 0, y: 30}}
                            whileInView={{opacity: 1, y: 0}}
                            transition={{duration: 0.6}}
                            viewport={{once: true}}
                        >
                            <CtaTitle>
                                {t('home.cta.title', 'Prêt à Transformer Votre Entreprise ?')}
                            </CtaTitle>
                            <CtaText>
                                {t('home.cta.subtitle', 'Contactez-nous dès aujourd\'hui pour discuter de vos besoins et découvrir comment nos solutions industrielles peuvent améliorer votre efficacité opérationnelle.')}
                            </CtaText>
                        </motion.div>

                        <motion.div
                            initial={{opacity: 0, y: 30}}
                            whileInView={{opacity: 1, y: 0}}
                            transition={{duration: 0.6, delay: 0.2}}
                            viewport={{once: true}}
                        >
                            <CtaButtons>
                                <CtaButton to="/contact" className="btn-secondary"
                                           onClick={() => handleLinkClick('/contact')}
                                >
                                    {t('common.contactUs', 'Contactez-Nous')}
                                </CtaButton>
                                <CtaButton to="/services" className="btn-outline"
                                           onClick={() => handleLinkClick('/services')}
                                >
                                    {t('services.title', 'Nos Services')}
                                </CtaButton>
                            </CtaButtons>
                        </motion.div>
                    </CtaContent>
                </div>
            </CtaSection>
        </>
    );
};

// Styled components
const AboutSection = styled.section`
    padding: 100px 0;
    background-color: var(--white);
`;

const AboutGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 50px;
    align-items: center;

    @media (max-width: 992px) {
        grid-template-columns: 1fr;
    }
`;

const AboutImage = styled(motion.div)`
    position: relative;

    img {
        width: 100%;
        border-radius: 10px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    }

    &::before {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border: 2px solid var(--primary);
        border-radius: 10px;
        top: -20px;
        left: -20px;
        z-index: -1;

        @media (max-width: 768px) {
            display: none;
        }
    }
`;

const CtaSection = styled.section`
    padding: 100px 0;
    background-color: var(--primary);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 100%);
    }
`;

const CtaContent = styled.div`
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
`;

const CtaTitle = styled.h2`
    font-size: 2.5rem;
    font-weight: 700;
    color: white;
    margin-bottom: 20px;
`;

const CtaText = styled.p`
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 40px;
    line-height: 1.7;
`;

const CtaButtons = styled.div`
    display: flex;
    gap: 20px;
    justify-content: center;

    @media (max-width: 576px) {
        flex-direction: column;
        align-items: center;
    }
`;

const CtaButton = styled(Link)`
    display: inline-block;
    padding: 15px 35px;
    font-weight: 600;
    border-radius: 5px;
    text-decoration: none;
    transition: all 0.3s ease;

    &.btn-secondary {
        background-color: white;
        color: var(--primary);

        &:hover {
            background-color: var(--light);
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
    }

    &.btn-outline {
        background-color: transparent;
        color: white;
        border: 2px solid white;

        &:hover {
            background-color: white;
            color: var(--primary);
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
        }
    }
`;

export default HomePage;
